import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Upload, message, Input, Popconfirm, Spin, Form, Select, DatePicker, Tag, Modal } from 'antd';
import { ArrowLeftOutlined, UploadOutlined, DeleteOutlined, EyeOutlined, SaveOutlined, CameraOutlined, QrcodeOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import useCompanyNavigate from '../../utils/useCompanyNavigate';
import { departmentBatchApi, jadeTypeApi, photoTypeApi, categoryApi } from '../../services/api';
import { scanQrCode, isUrl } from '../../utils/qrScan';

const photoTypeNames = {
  1: '原石照片', 3: '成品照片', 4: '检测证书', 5: '切料照片', 6: '规划照片', 7: '手修照片', 8: '抛光照片', 9: '原石入库照片', 10: '切料标块照片', 11: '打柱照片', 12: '磨珠照片', 13: '穿珠子照片', 14: '半成品照片'
};

// 默认货品级照片类型ID（只在品类数据不可用时作为回退，不含半成品等可选类型）
const DEFAULT_PRODUCT_PHOTO_TYPES = [3, 4];

const BatchDetail = () => {
  const navigate = useCompanyNavigate();
  const { id } = useParams();
  const isNew = id === 'new';
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [productTotal, setProductTotal] = useState(0);
  const PAGE_SIZE = 20;
  const [uploading, setUploading] = useState({});
  const [jadeTypes, setJadeTypes] = useState([]);
  const [form] = Form.useForm();
  const [presetOptions, setPresetOptions] = useState({}); // { [photoType]: [...] }
  const [dynamicPhotoTypeNames, setDynamicPhotoTypeNames] = useState({}); // 动态照片类型名称
  const [dynamicProductTypes, setDynamicProductTypes] = useState([]); // 动态货品级类型对象列表
  const [categoryPhotoTypes, setCategoryPhotoTypes] = useState(null); // 品类关联的照片类型ID列表

  const deptAdmin = JSON.parse(localStorage.getItem('department_admin') || '{}');
  const canCreateBatch = deptAdmin.can_create_batch === 1;
  const allowedPhotoTypes = batch?.allowedPhotoTypes || (deptAdmin.photo_types ? deptAdmin.photo_types.split(',').map(Number) : []);

  // 合并硬编码和动态的照片类型名称
  const resolvedPhotoTypeNames = { ...photoTypeNames, ...dynamicPhotoTypeNames };

  // 获取当前批次的品类允许的货品级照片类型
  const productPhotoTypeIds = (() => {
    const allProductTypeIds = dynamicProductTypes.length > 0
      ? dynamicProductTypes.map(t => t.id)
      : DEFAULT_PRODUCT_PHOTO_TYPES;
    // 如果有品类数据，过滤出货品级且品类允许的类型
    if (categoryPhotoTypes !== null) {
      return allProductTypeIds.filter(id => categoryPhotoTypes.includes(id));
    }
    return allProductTypeIds;
  })();

  // 判断是否为货品级模式
  const isProductMode = allowedPhotoTypes.some(t => productPhotoTypeIds.includes(t));

  // 加载动态照片类型和品类数据
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ptRes, catRes] = await Promise.all([
          photoTypeApi.all().catch(() => ({ data: [] })),
          categoryApi.all().catch(() => ({ data: [] })),
        ]);
        const types = ptRes.data || [];
        // 更新名称映射
        const nameMap = {};
        types.forEach(t => { nameMap[t.id] = t.name; });
        setDynamicPhotoTypeNames(nameMap);
        // 更新货品级类型对象列表
        const prodTypes = types.filter(t => t.level === 'product' && t.status !== 0);
        if (prodTypes.length > 0) {
          setDynamicProductTypes(prodTypes);
        }
        // 更新品类映射
        const cMap = {};
        (catRes.data || []).forEach(c => { cMap[c.name] = c; });
        // 如果当前已有批次，根据品类设置
        if (batch?.category && cMap[batch.category]) {
          setCategoryPhotoTypes(cMap[batch.category].photo_types.split(',').map(Number).filter(n => !isNaN(n)));
        }
      } catch {}
    };
    fetchData();
  }, []);

  // 按照片类型批量加载预设描述（每个类型只请求一次）
  useEffect(() => {
    const fetchAllPresets = async () => {
      const typesToFetch = [...new Set([...allowedPhotoTypes])];
      const results = {};
      for (const photoType of typesToFetch) {
        try {
          const res = await departmentBatchApi.listPresetsByType(photoType);
          results[photoType] = res.data || [];
        } catch {}
      }
      setPresetOptions(results);
    };
    if (allowedPhotoTypes.length > 0) {
      fetchAllPresets();
    }
  }, [batch?.allowedPhotoTypes, deptAdmin.photo_types]);

  useEffect(() => {
    if (isNew) {
      fetchJadeTypes();
    } else {
      fetchBatch();
    }
  }, [id]);

  const fetchJadeTypes = async () => {
    try {
      const res = await jadeTypeApi.all();
      setJadeTypes(res.data.map(j => ({ label: j.name, value: j.id })));
    } catch (err) {
      console.error('获取料种列表失败:', err);
    }
  };

  const fetchBatch = async () => {
    setLoading(true);
    try {
      const res = await departmentBatchApi.detail(id);
      setBatch(res.data);
      // 根据批次品类更新允许的货品级照片类型
      if (res.data?.category) {
        try {
          const catRes = await categoryApi.all();
          const cat = (catRes.data || []).find(c => c.name === res.data.category);
          if (cat?.photo_types) {
            setCategoryPhotoTypes(cat.photo_types.split(',').map(Number).filter(n => !isNaN(n)));
          }
        } catch {}
      }
      // 如果是货品级模式，加载货品列表
      const types = res.data?.allowedPhotoTypes || (deptAdmin.photo_types ? deptAdmin.photo_types.split(',').map(Number) : []);
      if (types.some(t => productPhotoTypeIds.includes(t))) {
        fetchProducts();
      }
    } catch (err) {
      message.error('获取批次详情失败');
      navigate('/department');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (page = 1, append = false) => {
    if (page === 1) {
      setProductsLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const res = await departmentBatchApi.getBatchProducts(id, { page, limit: PAGE_SIZE });
      const list = res.data?.list || res.data || [];
      const pagination = res.data?.pagination;
      if (append) {
        setProducts(prev => [...prev, ...list]);
      } else {
        setProducts(list);
      }
      setProductPage(page);
      setHasMore(pagination ? pagination.hasMore : list.length >= PAGE_SIZE);
      setProductTotal(pagination ? pagination.total : 0);
    } catch (err) {
      message.error('获取货品列表失败');
    } finally {
      setProductsLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreProducts = () => {
    if (!loadingMore && hasMore) {
      fetchProducts(productPage + 1, true);
    }
  };

  const handleCreate = async (values) => {
    setLoading(true);
    try {
      const data = {
        ...values,
        rawMaterialDate: values.rawMaterialDate ? values.rawMaterialDate.format('YYYY-MM-DD') : null,
      };
      const res = await departmentBatchApi.create(data);
      message.success('创建成功');
      navigate(`/department/batches/${res.data.id}`, { replace: true });
    } catch (err) {
      message.error(err.response?.data?.message || '创建失败');
    } finally {
      setLoading(false);
    }
  };

// 记录用户开始操作（点击选择/拍照）时的滚动位置
const operationScrollY = useRef(0);

const refreshWithScroll = async (refreshFn) => {
  const scrollY = operationScrollY.current || window.scrollY;
  await refreshFn();
  // 多次尝试恢复滚动位置，确保在DOM更新后生效
  const restore = () => window.scrollTo(0, scrollY);
  restore();
  requestAnimationFrame(restore);
  setTimeout(restore, 100);
};

  const handleBatchUpload = async (photoType, fileList, descriptions = {}) => {
    if (fileList.length === 0) {
      message.warning('请选择要上传的文件');
      return;
    }
    setUploading(prev => ({ ...prev, [`batch_${photoType}`]: true }));
    const formData = new FormData();
    fileList.forEach(file => formData.append('photos', file));
    formData.append('photoType', photoType);
    const descArray = fileList.map((_, i) => descriptions[i] || '');
    formData.append('descriptions', JSON.stringify(descArray));

    try {
      await departmentBatchApi.uploadPhotos(id, formData);
      message.success('上传成功');
      refreshWithScroll(() => fetchBatch());
    } catch (err) {
      message.error(err.response?.data?.message || '上传失败');
    } finally {
      setUploading(prev => ({ ...prev, [`batch_${photoType}`]: false }));
    }
  };

  const handleProductUpload = async (productId, photoType, fileList, descriptions = {}) => {
    if (fileList.length === 0) {
      message.warning('请选择要上传的文件');
      return;
    }
    setUploading(prev => ({ ...prev, [`product_${productId}_${photoType}`]: true }));
    const formData = new FormData();
    fileList.forEach(file => formData.append('photos', file));
    formData.append('photoType', photoType);
    const descArray = fileList.map((_, i) => descriptions[i] || '');
    formData.append('descriptions', JSON.stringify(descArray));

    try {
      await departmentBatchApi.uploadProductPhotos(productId, formData);
      message.success('上传成功');
      refreshWithScroll(() => fetchProducts(1, false));
    } catch (err) {
      message.error(err.response?.data?.message || '上传失败');
    } finally {
      setUploading(prev => ({ ...prev, [`product_${productId}_${photoType}`]: false }));
    }
  };

  const handleBatchDelete = async (photoId) => {
    try {
      await departmentBatchApi.deletePhoto(photoId);
      message.success('删除成功');
      refreshWithScroll(() => fetchBatch());
    } catch (err) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  const handleProductDelete = async (photoId) => {
    try {
      await departmentBatchApi.deleteProductPhoto(photoId);
      message.success('删除成功');
      refreshWithScroll(() => fetchProducts(1, false));
    } catch (err) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  // 新建批次
  if (isNew && canCreateBatch) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/department')}>返回</Button>
          <h3 style={{ margin: 0 }}>新建批次</h3>
        </div>
        <Card>
          <Form form={form} layout="vertical" onFinish={handleCreate}>
            <Form.Item name="batchNo" label="批次编号" rules={[{ required: true, message: '请输入批次编号' }]}>
              <Input placeholder="如: HT2024001" />
            </Form.Item>
            <Form.Item name="name" label="批次名称" rules={[{ required: true, message: '请输入批次名称' }]}>
              <Input placeholder="请输入批次名称" />
            </Form.Item>
            <Form.Item name="jadeTypeId" label="料种">
              <Select placeholder="请选择料种" options={jadeTypes} allowClear />
            </Form.Item>
            <Form.Item name="rawMaterialOrigin" label="原石产地">
              <Input placeholder="如: 新疆和田" />
            </Form.Item>
            <Form.Item name="rawMaterialDate" label="原石日期">
              <DatePicker style={{ width: '100%' }} placeholder="选择日期" />
            </Form.Item>
            <Form.Item name="description" label="描述/备注">
              <Input.TextArea rows={3} placeholder="请输入批次描述" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading} style={{ background: '#8B7355' }}>
                创建批次
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  if (loading) {
    return <div style={{ textAlign: 'center', marginTop: 60 }}><Spin size="large" /></div>;
  }

  if (!batch) {
    return <div style={{ textAlign: 'center', marginTop: 60 }}>批次不存在</div>;
  }

  // 批次级照片类型（非货品级的）
  const batchPhotoTypes = allowedPhotoTypes.filter(t => !productPhotoTypeIds.includes(t));
  // 货品级照片类型
  const prodPhotoTypes = allowedPhotoTypes.filter(t => productPhotoTypeIds.includes(t));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/department')}>返回</Button>
        <div>
          <h3 style={{ margin: 0, fontSize: 16 }}>{batch.batch_no} - {batch.name}</h3>
          {batch.jade_type_name && <span style={{ fontSize: 12, color: '#999' }}>料种：{batch.jade_type_name}</span>}
        </div>
      </div>

      {/* 批次级照片（原石、切料等） */}
      {batchPhotoTypes.map(photoType => {
        const photos = batch.photos?.[String(photoType)] || [];
        return (
          <Card
            key={photoType}
            title={resolvedPhotoTypeNames[photoType] || `类型${photoType}`}
            size="small"
            style={{ marginBottom: 12, borderRadius: 8 }}
          >
            <PhotoUploadSection
              photoType={photoType}
              photos={photos}
              uploading={uploading[`batch_${photoType}`]}
              onUpload={(files, descriptions) => handleBatchUpload(photoType, files, descriptions)}
              onDelete={handleBatchDelete}
              presetOptions={presetOptions[photoType] || []}
              operationScrollY={operationScrollY}
            />
          </Card>
        );
      })}

      {/* 货品级照片（成品、证书等） */}
      {isProductMode && (
        <Card
          title={`货品列表（${prodPhotoTypes.map(t => resolvedPhotoTypeNames[t]).join('、')}）${productTotal > 0 ? ` · 共${productTotal}件` : ''}`}
          size="small"
          style={{ marginBottom: 12, borderRadius: 8 }}
        >
          {productsLoading ? (
            <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>该批次下暂无货品</div>
          ) : (
            <>
              {products.map(product => (
                <Card
                  key={product.id}
                  size="small"
                  style={{ marginBottom: 8, borderRadius: 6, background: '#fafafa' }}
                  title={
                    <div style={{ fontSize: 14 }}>
                      <span style={{ fontWeight: 600 }}>{product.product_code}</span>
                      <span style={{ color: '#999', marginLeft: 8 }}>{product.name}</span>
                      {product.category && <Tag style={{ marginLeft: 8 }}>{product.category}</Tag>}
                      {product.weight && <span style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>{product.weight}g</span>}
                    </div>
                  }
                >
                  {prodPhotoTypes.map(photoType => {
                    const photos = product.photos?.[photoType] || [];
                    return (
                      <div key={photoType} style={{ marginBottom: photoType === prodPhotoTypes[prodPhotoTypes.length - 1] ? 0 : 12 }}>
                        <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 4, color: '#666' }}>
                          {resolvedPhotoTypeNames[photoType]}
                        </div>
                        <PhotoUploadSection
                          photoType={photoType}
                          photos={photos}
                          uploading={uploading[`product_${product.id}_${photoType}`]}
                          onUpload={(files, descriptions) => handleProductUpload(product.id, photoType, files, descriptions)}
                          onDelete={handleProductDelete}
                          presetOptions={presetOptions[photoType] || []}
                          compact
                          operationScrollY={operationScrollY}
                        />
                      </div>
                    );
                  })}
                </Card>
              ))}
              {hasMore && (
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <Button onClick={loadMoreProducts} loading={loadingMore} block>
                    加载更多（已加载{products.length}/{productTotal}）
                  </Button>
                </div>
              )}
              {!hasMore && products.length > PAGE_SIZE && (
                <div style={{ textAlign: 'center', color: '#999', fontSize: 12, padding: '8px 0' }}>
                  已全部加载，共{productTotal}件
                </div>
              )}
            </>
          )}
        </Card>
      )}
    </div>
  );
};

// 照片上传子组件
const PhotoUploadSection = ({ photoType, photos, uploading, onUpload, onDelete, compact, presetOptions: presetOptionsProp, operationScrollY }) => {
  const [fileList, setFileList] = useState([]);
  const [fileDescriptions, setFileDescriptions] = useState({});
  const cameraInputRef = useRef(null);

  const presetOptions = presetOptionsProp || [];

  // 选择文件或拍照后，fileList变化时恢复滚动位置
  useEffect(() => {
    if (fileList.length > 0 && operationScrollY?.current > 0) {
      window.scrollTo(0, operationScrollY.current);
    }
  }, [fileList]);

  const uploadProps = {
    onChange: ({ fileList: newFileList }) => {
      if (newFileList.length > fileList.length) {
        // 文件增加时已经有 operationScrollY 了，不需要再记录
      }
      setFileList(newFileList);
    },
    beforeUpload: () => false,
    fileList,
    multiple: true,
    accept: 'image/*',
    listType: 'picture-card',
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileList(prev => [...prev, file]);
    }
    e.target.value = '';
  };

  const handleSubmit = () => {
    const rawFiles = fileList.map(f => f.originFileObj || f);
    onUpload(rawFiles, fileDescriptions);
    setFileList([]);
    setFileDescriptions({});
  };

  const handleScanQr = async (photo) => {
    const hide = message.loading('扫描二维码中...', 0);
    try {
      const result = await scanQrCode(photo.id);
      hide();
      if (result && isUrl(result)) {
        Modal.info({
          title: '检测到二维码链接',
          content: (
            <div>
              <p style={{ wordBreak: 'break-all', marginBottom: 12 }}>{result}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="primary" size="small" onClick={() => window.open(result, '_blank')}>打开链接</Button>
                <Button size="small" onClick={() => { navigator.clipboard.writeText(result); message.success('已复制'); }}>复制链接</Button>
              </div>
            </div>
          ),
          okText: '关闭',
        });
      } else if (result) {
        Modal.info({
          title: '检测到二维码内容',
          content: (
            <div>
              <p style={{ wordBreak: 'break-all', marginBottom: 12 }}>{result}</p>
              <Button size="small" onClick={() => { navigator.clipboard.writeText(result); message.success('已复制'); }}>复制内容</Button>
            </div>
          ),
          okText: '关闭',
        });
      } else {
        message.warning('该证书照片中未检测到二维码');
      }
    } catch {
      hide();
      message.error('扫描失败');
    }
  };

  const thumbSize = compact ? 60 : 80;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Upload {...uploadProps}>
          {fileList.length < 20 && (
            <div style={{ width: thumbSize, height: thumbSize, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} onClick={() => { if (operationScrollY) operationScrollY.current = window.scrollY; }}>
              <UploadOutlined />
              <div style={{ marginTop: 4, fontSize: 11 }}>选择</div>
            </div>
          )}
        </Upload>
        <Button icon={<CameraOutlined />} size="small" onClick={() => { if (operationScrollY) operationScrollY.current = window.scrollY; cameraInputRef.current?.click(); }}>
          拍照
        </Button>
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleCameraCapture} />
      </div>

      {fileList.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {presetOptions.length > 0 && fileList.length > 1 && (
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: '#999', marginRight: 8 }}>批量设置：</span>
              {presetOptions.map(p => (
                <Tag key={p.id} style={{ fontSize: 11, cursor: 'pointer', marginBottom: 4 }} onClick={() => {
                  setFileDescriptions(prev => {
                    const next = { ...prev };
                    fileList.forEach((_, i) => { next[i] = p.description; });
                    return next;
                  });
                }}>
                  {p.description}
                </Tag>
              ))}
            </div>
          )}
          {fileList.map((file, index) => (
            <div key={file.uid || index} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
              <img src={URL.createObjectURL(file.originFileObj || file)} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Input placeholder="输入或选择描述..." value={fileDescriptions[index] || ''} onChange={e => setFileDescriptions(prev => ({ ...prev, [index]: e.target.value }))} size="small" allowClear />
                {presetOptions.length > 0 && (
                  <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {presetOptions.map(p => (
                      <Tag key={p.id} color={fileDescriptions[index] === p.description ? '#8B7355' : undefined} style={{ fontSize: 11, cursor: 'pointer' }} onClick={() => setFileDescriptions(prev => ({ ...prev, [index]: p.description }))}>
                        {p.description}
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <Button type="primary" size="small" onClick={handleSubmit} loading={uploading} style={{ background: '#8B7355' }}>
            上传 ({fileList.length}张)
          </Button>
        </div>
      )}

      {photos.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {photos.map(photo => (
            <div key={photo.id} style={{ textAlign: 'center' }}>
              <div style={{ position: 'relative', width: thumbSize, height: thumbSize, borderRadius: 6, overflow: 'hidden', border: '1px solid #f0f0f0' }}>
                <img src={photo.url || photo.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '1px 4px', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <a href={photo.url} target="_blank" rel="noreferrer" style={{ color: '#fff', fontSize: 11 }}><EyeOutlined /></a>
                    {photoType === 4 && (
                      <span onClick={() => handleScanQr(photo)} style={{ color: '#fff', fontSize: 11, cursor: 'pointer' }} title="扫描二维码"><QrcodeOutlined /></span>
                    )}
                  </div>
                  <Popconfirm title="删除这张照片?" onConfirm={() => onDelete(photo.id)}>
                    <span style={{ color: '#ff4d4f', fontSize: 11, cursor: 'pointer' }} onClick={() => { if (operationScrollY) operationScrollY.current = window.scrollY; }}><DeleteOutlined /></span>
                  </Popconfirm>
                </div>
              </div>
              {photo.description && (
                <div style={{ width: thumbSize, fontSize: 9, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                  {photo.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {photos.length === 0 && fileList.length === 0 && (
        <div style={{ textAlign: 'center', color: '#ccc', padding: '10px 0', fontSize: 12 }}>暂无照片</div>
      )}
    </div>
  );
};

export default BatchDetail;
